import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";

const LEADING_COLUMNS = [
  { key: "_detail", label: "ID", width: 60 },
  { key: "created_at", label: "Date", width: 160 },
  { key: "campaign_name", label: "Campaign", width: 160 },
  { key: "variant_name", label: "Variant", width: 70 },
  { key: "element_type", label: "Element", width: 100 },
];

const TRAILING_CONTACT_COLUMNS = [
  { key: "contact_email", label: "Email", width: 140 },
  { key: "contact_phone", label: "Phone", width: 120 },
  { key: "contact_name", label: "Name", width: 160 },
];
const ACTIONS_COLUMN = { key: "_actions", label: "", width: 130, isAction: true };

const HIDDEN_DYNAMIC_FIELD_SLUGS = new Set(["variant_id"]);
const ALWAYS_FIRST_DYNAMIC_SLUGS = ["source_url"];
const PRIORITY_DYNAMIC_SLUG_ORDER = ["url", "latest_score"];

const CONTACT_PRESET_FIELD_SLUG_ORDER = [
  "address",
  "city",
  "company_name",
  "company_title",
  "country",
  "email",
  "last_name",
  "name",
  "phone",
  "source_url",
  "state",
  "url",
  "zip_code",
];

const CONTACT_PRESET_FIELD_SLUGS = new Set(CONTACT_PRESET_FIELD_SLUG_ORDER);

function parseSearchFields(params) {
  const searchFields = {};

  for (const [key, value] of params.entries()) {
    const match = key.match(/^search_fields\[(.+)\]$/);
    if (!match) continue;

    const fieldSlug = match[1];
    if (!fieldSlug || !value) continue;

    searchFields[fieldSlug] = value;
  }

  return searchFields;
}

// Read filter params from current URL (set by the ERB sidebar form)
function getFiltersFromURL() {
  const params = new URLSearchParams(window.location.search);
  return {
    contactable_id: params.get("contactable_id") || "",
    variant_id: params.get("variant_id") || "",
    step_id: params.get("step_id") || "",
    element_id: params.get("element_id") || "",
    timeframe: params.get("timeframe") || "",
    search: params.get("search") || "",
    search_fields: parseSearchFields(params),
    range_start: params.get("range_start") || "",
    range_end: params.get("range_end") || "",
    submission_id: params.get("submission_id") || "",
  };
}

const SubmissionsTable = (props) => {
  const { website_id, field_columns, can_manage_submissions } = props;

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const filters = getFiltersFromURL();
  const hasElementFilter = Boolean(filters.element_id);

  const baseDynamicFieldColumns = useMemo(
    () =>
      (field_columns || [])
        .filter(
          (column) =>
            column &&
            column.slug &&
            !HIDDEN_DYNAMIC_FIELD_SLUGS.has(column.slug)
        )
        .map((column) => ({
          key: `field_${column.slug}`,
          slug: column.slug,
          label: column.slug,
          width: Math.max(100, column.slug.length * 8 + 24),
        })),
    [field_columns]
  );

  const elementFieldSlugs = useMemo(() => {
    if (!hasElementFilter) return null;

    const slugs = new Set();

    submissions.forEach((submission) => {
      Object.keys(submission.fields || {}).forEach((slug) => {
        if (!slug) return;
        slugs.add(slug);
      });
    });

    return slugs;
  }, [hasElementFilter, submissions]);

  const isFormElementFilter = useMemo(() => {
    if (!hasElementFilter) return false;

    return submissions.some((submission) =>
      String(submission.element_type || "")
        .toLowerCase()
        .includes("form")
    );
  }, [hasElementFilter, submissions]);

  const dynamicFieldColumns = useMemo(() => {
    const withPriorityFieldsFirst = (columns) => {
      const priorityColumns = PRIORITY_DYNAMIC_SLUG_ORDER.map((slug) =>
        columns.find((column) => column.slug === slug)
      ).filter(Boolean);
      const withoutPriority = columns.filter(
        (column) => !PRIORITY_DYNAMIC_SLUG_ORDER.includes(column.slug)
      );
      return [...priorityColumns, ...withoutPriority];
    };

    if (!hasElementFilter || !elementFieldSlugs) {
      return withPriorityFieldsFirst(baseDynamicFieldColumns);
    }

    const scopedColumns = baseDynamicFieldColumns.filter((column) =>
      elementFieldSlugs.has(column.slug)
    );

    if (!isFormElementFilter) {
      const customColumns = scopedColumns.filter(
        (column) => !CONTACT_PRESET_FIELD_SLUGS.has(column.slug)
      );
      const presetColumns = scopedColumns.filter((column) =>
        CONTACT_PRESET_FIELD_SLUGS.has(column.slug)
      );
      return [...customColumns, ...presetColumns];
    }

    const presetColumns = CONTACT_PRESET_FIELD_SLUG_ORDER.map((slug) =>
      scopedColumns.find((column) => column.slug === slug)
    ).filter(Boolean);

    const customColumns = scopedColumns.filter(
      (column) => !CONTACT_PRESET_FIELD_SLUGS.has(column.slug)
    );

    return withPriorityFieldsFirst([...presetColumns, ...customColumns]);
  }, [
    baseDynamicFieldColumns,
    elementFieldSlugs,
    hasElementFilter,
    isFormElementFilter,
  ]);

  const alwaysFirstDynamicColumns = ALWAYS_FIRST_DYNAMIC_SLUGS.map((slug) =>
    dynamicFieldColumns.find((column) => column.slug === slug)
  ).filter(Boolean);
  const priorityDynamicColumns = PRIORITY_DYNAMIC_SLUG_ORDER.map((slug) =>
    dynamicFieldColumns.find((column) => column.slug === slug)
  ).filter(Boolean);
  const priorityDynamicColumnsWithoutAlwaysFirst = priorityDynamicColumns.filter(
    (column) => !ALWAYS_FIRST_DYNAMIC_SLUGS.includes(column.slug)
  );
  const priorityDynamicCustomColumns = priorityDynamicColumns.filter(
    (column) => !CONTACT_PRESET_FIELD_SLUGS.has(column.slug)
  );
  const priorityDynamicPresetColumns = priorityDynamicColumns.filter((column) =>
    CONTACT_PRESET_FIELD_SLUGS.has(column.slug)
  );
  const dynamicColumnsWithoutPriority = dynamicFieldColumns.filter(
    (column) =>
      !ALWAYS_FIRST_DYNAMIC_SLUGS.includes(column.slug) &&
      !PRIORITY_DYNAMIC_SLUG_ORDER.includes(column.slug)
  );
  const dynamicCustomColumnsWithoutPriority = dynamicColumnsWithoutPriority.filter(
    (column) => !CONTACT_PRESET_FIELD_SLUGS.has(column.slug)
  );
  const dynamicPresetColumnsWithoutPriority = dynamicColumnsWithoutPriority.filter(
    (column) => CONTACT_PRESET_FIELD_SLUGS.has(column.slug)
  );
  const allColumns =
    hasElementFilter && !isFormElementFilter
      ? [
          ...LEADING_COLUMNS,
          ...alwaysFirstDynamicColumns,
          ...priorityDynamicCustomColumns,
          ...dynamicCustomColumnsWithoutPriority,
          ...priorityDynamicPresetColumns,
          ...TRAILING_CONTACT_COLUMNS,
          ...dynamicPresetColumnsWithoutPriority,
          ACTIONS_COLUMN,
        ]
      : [
          ...LEADING_COLUMNS,
          ...alwaysFirstDynamicColumns,
          ...priorityDynamicColumnsWithoutAlwaysFirst,
          ...TRAILING_CONTACT_COLUMNS,
          ...dynamicColumnsWithoutPriority,
          ACTIONS_COLUMN,
        ];

  const buildParams = useCallback(
    (cursor) => {
      const params = new URLSearchParams();
      if (filters.contactable_id) params.set("contactable_id", filters.contactable_id);
      if (filters.variant_id) params.set("variant_id", filters.variant_id);
      if (filters.step_id) params.set("step_id", filters.step_id);
      if (filters.element_id) params.set("element_id", filters.element_id);
      if (filters.search) params.set("search", filters.search);
      if (filters.search_fields) {
        Object.entries(filters.search_fields).forEach(([slug, value]) => {
          if (slug && value) params.set(`search_fields[${slug}]`, value);
        });
      }
      if (cursor) params.set("cursor", cursor);

      if (filters.range_start && filters.range_end) {
        params.set("range_start", filters.range_start);
        params.set("range_end", filters.range_end);
      } else if (filters.timeframe) {
        params.set("timeframe", filters.timeframe);
      }

      if (filters.submission_id) params.set("submission_id", filters.submission_id);

      return params.toString();
    },
    [filters]
  );

  const fetchSubmissions = useCallback(
    (cursor, append) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const queryString = buildParams(cursor);

      fetch(`/websites/${website_id}/events/submissions.json?${queryString}`)
        .then((res) => res.json())
        .then((data) => {
          if (append) {
            setSubmissions((prev) => [...prev, ...(data.submissions || [])]);
          } else {
            let nextSubmissions = data.submissions || [];
            if (data.open_submission) {
              const hasSubmission = nextSubmissions.some(
                (submission) => submission.id === data.open_submission.id
              );
              if (!hasSubmission) {
                nextSubmissions = [data.open_submission, ...nextSubmissions];
              }
              setExpandedRow(data.open_submission.id);
            }
            setSubmissions(nextSubmissions);
          }
          setNextCursor(data.next_cursor);
          setHasMore(data.has_more);
          setLoading(false);
          setLoadingMore(false);
        })
        .catch(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [website_id, buildParams]
  );

  useEffect(() => {
    fetchSubmissions(null, false);
  }, []);

  const loadMore = () => {
    if (hasMore && nextCursor) {
      fetchSubmissions(nextCursor, true);
    }
  };
  const showInitialLoader = loading && submissions.length === 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const builderUrl = (sub) => {
    if (sub.campaign_id && sub.variant_id) {
      return `/websites/${website_id}/cta/${sub.campaign_id}/variants/${sub.variant_id}/edit`;
    }
    return null;
  };

  const campaignUrl = (sub) => {
    if (sub.campaign_id) {
      return `/websites/${website_id}/cta/${sub.campaign_id}`;
    }
    return null;
  };

  const handleDeleteSubmission = async (submissionId) => {
    const shouldDelete = window.confirm("Are you sure you want to delete this submission?");
    if (!shouldDelete) return;

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    try {
      const response = await fetch(`/websites/${website_id}/events/${submissionId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
          Accept: "application/json",
        },
      });

      if (!response.ok) return;

      setSubmissions((prev) => prev.filter((submission) => submission.id !== submissionId));
      setExpandedRow((prev) => (prev === submissionId ? null : prev));
    } catch (_error) {
      // noop
    }
  };

  const renderOptionsDropdown = (submission, { compact = false } = {}) => (
    <div
      className="btn-group"
      style={{ float: "right" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`bg-white border-solid border-[1px] rounded-md border-slate-200 text-black text-xl hover:bg-blue-100 hover:border-blue-100 hover:text-blue-600 ${
          compact ? "py-2 px-4" : "py-3 px-5 mr-4"
        }`}
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        Options <span className="caret"></span>
      </button>
      <ul className="dropdown-menu submissions-actions-menu">
        <li>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpandedRow(submission.id);
            }}
          >
            View
          </a>
        </li>
        {can_manage_submissions && (
          <li>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteSubmission(submission.id);
              }}
            >
              Delete
            </a>
          </li>
        )}
      </ul>
    </div>
  );

  const getCellValue = (submission, column) => {
    if (column.key === "_detail") {
      return submission.id || "";
    }
    if (column.key === "_actions") {
      return renderOptionsDropdown(submission, { compact: true });
    }
    if (column.slug) {
      const val = submission.fields?.[column.slug];
      if (val === null || val === undefined) return "";
      if (typeof val === "object") return JSON.stringify(val);
      return String(val);
    }
    if (column.key === "created_at") {
      return formatDate(submission.created_at);
    }
    if (column.key === "campaign_name" && submission.campaign_name) {
      const href = campaignUrl(submission);
      if (href) {
        return (
          <a href={href} onClick={(e) => e.stopPropagation()} style={{ color: "#4f46e5", textDecoration: "none" }}>
            {submission.campaign_name}
          </a>
        );
      }
    }
    if ((column.key === "variant_name" || column.key === "element_type") && submission[column.key]) {
      const href = builderUrl(submission);
      if (href) {
        return (
          <a href={href} onClick={(e) => e.stopPropagation()} style={{ color: "#4f46e5", textDecoration: "none" }}>
            {submission[column.key]}
          </a>
        );
      }
    }
    return submission[column.key] || "";
  };

  const renderModal = () => {
    if (expandedRow === null) return null;
    const sub = submissions.find((s) => s.id === expandedRow);
    if (!sub) return null;

    return (
      <div
        className="submissions-modal-overlay"
        onClick={() => setExpandedRow(null)}
      >
        <div
          className="submissions-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="close submissions-modal-close"
            aria-label="Close"
            onClick={() => setExpandedRow(null)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <div className="submissions-modal-body">
            <div className="submissions-modal-header">
              <h4 className="submissions-modal-title">Submission #{sub.id}</h4>
              <div className="submissions-modal-actions">
                {can_manage_submissions && (
                  <button
                    type="button"
                    className="bg-white border-solid border-[1px] py-3 px-4 rounded-md border-slate-200 text-black text-xl hover:bg-blue-100 hover:border-blue-100 hover:text-blue-600 inline-flex items-center justify-center gap-4"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteSubmission(sub.id);
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
            <div className="submissions-modal-section">
              <h5 style={{ fontWeight: 600, marginBottom: 12 }}>Event Info</h5>
              <div className="submissions-modal-grid">
                <div className="submissions-modal-field">
                  <p className="field-title bold">Date</p>
                  <p className="text-xl light">{formatDate(sub.created_at)}</p>
                </div>
                <div className="submissions-modal-field">
                  <p className="field-title bold">Campaign</p>
                  {campaignUrl(sub) && sub.campaign_id ? (
                    <p className="text-xl light">
                      <a href={campaignUrl(sub)} className="text-xl light" style={{ color: "#4f46e5", textDecoration: "none" }}>
                        {sub.campaign_name || `Campaign #${sub.campaign_id}`}
                      </a>
                    </p>
                  ) : (
                    <p className="text-xl light">{sub.campaign_name || "–"}</p>
                  )}
                </div>
                <div className="submissions-modal-field">
                  <p className="field-title bold">Variant</p>
                  {builderUrl(sub) && sub.variant_id ? (
                    <p className="text-xl light">
                      <a href={builderUrl(sub)} className="text-xl light" style={{ color: "#4f46e5", textDecoration: "none" }}>
                        {sub.variant_name || `Variant #${sub.variant_id}`}
                      </a>
                    </p>
                  ) : (
                    <p className="text-xl light">{sub.variant_name || "–"}</p>
                  )}
                </div>
                <div className="submissions-modal-field">
                  <p className="field-title bold">Element</p>
                  {builderUrl(sub) && sub.element_id ? (
                    <p className="text-xl light">
                      <a href={builderUrl(sub)} className="text-xl light" style={{ color: "#4f46e5", textDecoration: "none" }}>
                        {sub.element_type || `Element #${sub.element_id}`}
                      </a>
                    </p>
                  ) : (
                    <p className="text-xl light">{sub.element_type || "–"}</p>
                  )}
                </div>
                <div className="submissions-modal-field">
                  <p className="field-title bold">Page URL</p>
                  <p className="text-xl light">{sub.url || "–"}</p>
                </div>
                {sub.contact_id && (
                  <div className="submissions-modal-field">
                    <p className="field-title bold">Contact</p>
                    <p className="text-xl light">
                      <a href={`/websites/${website_id}/contacts/${sub.contact_id}`} className="text-xl light">
                        {sub.contact_email || sub.contact_name || `Contact #${sub.contact_id}`}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="submissions-modal-section">
              <h5 style={{ fontWeight: 600, marginBottom: 12 }}>
                Submitted Fields
              </h5>
              {Object.entries(sub.fields || {}).map(([key, value]) => (
                <div key={key} className="submissions-submitted-field">
                  <p
                    className="field-title bold"
                    style={{ textTransform: "none", marginBottom: 4 }}
                  >
                    {key}
                  </p>
                  <p className="text-xl light" style={{ marginBottom: 0, wordWrap: "break-word" }}>
                    {typeof value === "object" ? JSON.stringify(value) : String(value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {showInitialLoader ? (
        <div className="submissions-table-loading submissions-list-row-container some-vertical-space border-solid border-[1px] border-slate-200 rounded-lg">
          <span className="cf-loading-spinner reports-spinner submissions-table-loading-spinner"></span>
          <p className="text-slate-500 text-xl mb-0">Loading submissions...</p>
        </div>
      ) : (
        <div
          className="submissions-table-container submissions-list-row-container some-vertical-space border-solid border-[1px] border-slate-200 rounded-lg"
          style={{
            overflowX: "auto",
            overflowY: "visible",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 #f8fafc",
          }}
        >
          <table
            className="table submissions-list-row-table"
            style={{
              marginBottom: 0,
              fontSize: 12,
              tableLayout: "fixed",
              minWidth: allColumns.reduce((sum, c) => sum + c.width, 0),
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr className="submissions-list-row-header">
                {allColumns.map((col) => (
                  <th
                    key={col.key}
                    className="submissions-list-row-heading"
                    style={{
                      width: col.width,
                      minWidth: col.width,
                      padding: col.isIcon ? "10px 8px" : "10px 12px",
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#000000",
                      textTransform: "none",
                      letterSpacing: 0,
                      borderBottom: "1px solid #f1f1f1",
                      whiteSpace: "nowrap",
                      textAlign: col.isIcon ? "center" : col.isAction ? "right" : "left",
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && (
                <tr>
                  <td
                    colSpan={allColumns.length}
                    style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}
                  >
                    No submissions found
                  </td>
                </tr>
              )}
              {submissions.map((sub) => (
                <tr
                  key={sub.id}
                  onClick={() => setExpandedRow(sub.id)}
                  className="submissions-table-row submissions-list-row-item"
                  style={{
                    cursor: "pointer",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {allColumns.map((col) => (
                    <td
                      key={col.key}
                      onClick={col.isAction ? (e) => e.stopPropagation() : undefined}
                      style={{
                        padding: col.isIcon ? "10px 8px" : "10px 12px",
                        width: col.width,
                        minWidth: col.width,
                        overflow: col.isAction ? "visible" : "hidden",
                        textOverflow: col.isAction ? "clip" : "ellipsis",
                        whiteSpace: col.isAction ? "normal" : "nowrap",
                        borderBottom: "1px solid #f1f1f1",
                        textAlign: col.isIcon ? "center" : col.isAction ? "right" : "left",
                        color: "#000000",
                        fontSize: 13,
                      }}
                      title={col.isIcon || col.isAction ? "" : String(getCellValue(sub, col) || "")}
                    >
                      {getCellValue(sub, col)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && !loading && (
        <div
          style={{
            padding: "16px",
            textAlign: "center",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <button
            className="btn btn-default"
            onClick={loadMore}
            disabled={loadingMore}
            style={{ fontSize: 13 }}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      {renderModal()}

      <style>{`
        .submissions-list-row-header {
          background: #ffffff;
        }
        .submissions-list-row-heading {
          font-size: 12px;
        }
        .submissions-list-row-table {
          margin-top: 0;
        }
        .submissions-list-row-item:last-child td {
          border-bottom: 0;
        }
        .submissions-table-container::-webkit-scrollbar {
          display: block;
          height: 10px;
        }
        .submissions-table-container::-webkit-scrollbar-track {
          background: #f8fafc;
        }
        .submissions-table-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 5px;
        }
        .submissions-table-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .submissions-table-row:hover {
          background-color: #f5f7f9 !important;
        }
        .submissions-table-loading {
          min-height: 260px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 12px;
          background: #fff;
        }
        .submissions-table-loading-spinner {
          width: 20px;
          height: 20px;
        }
        .submissions-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
          z-index: 1050;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          overflow-y: auto;
          padding: 40px 0;
        }
        .submissions-modal {
          background: #fff;
          border-radius: 12px;
          width: 720px;
          max-width: 90vw;
          max-height: none;
          overflow: visible;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          position: relative;
        }
        .submissions-modal-close {
          position: absolute;
          top: 12px;
          right: 16px;
          opacity: 1;
          z-index: 2;
        }
        .submissions-modal-body {
          padding: 24px;
        }
        .submissions-modal-header {
          align-items: center;
          display: flex;
          justify-content: space-between;
          margin-bottom: 16px;
          padding-right: 32px;
        }
        .submissions-modal-title {
          font-weight: 600;
          margin: 0;
        }
        .submissions-modal-actions {
          display: flex;
          justify-content: flex-end;
        }
        .submissions-actions-menu {
          border: none;
          border-radius: 8px;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.15);
          min-width: 140px;
          z-index: 1080;
        }
        .submissions-modal-section {
          margin-bottom: 24px;
        }
        .submissions-modal-section:last-child {
          margin-bottom: 0;
        }
        .submissions-modal-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .submissions-modal-field {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .submissions-modal-field p {
          margin: 0;
        }
        .submissions-modal-section h5 {
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 8px;
          margin-bottom: 12px !important;
        }
        .submissions-modal-section + .submissions-modal-section h5 {
          padding-top: 8px;
        }
        .submissions-modal-section:last-child {
          padding-bottom: 8px;
        }
        .submissions-submitted-field {
          margin-bottom: 16px;
        }
        .submissions-submitted-field:last-child {
          margin-bottom: 0;
        }
        .submissions-modal-label {
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
      `}</style>
    </>
  );
};

SubmissionsTable.propTypes = {
  website_id: PropTypes.number.isRequired,
  can_manage_submissions: PropTypes.bool,
  field_columns: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      label: PropTypes.string
    })
  )
};

export default SubmissionsTable;
